/**
 * Ficha de un usuario: datos, acciones sobre la cuenta y empresas que puede ver. Cada acción con
 * consecuencias se confirma en un diálogo que dice qué va a pasar; toda queda en la auditoría.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import {
  mensajeDeAccion,
  type Acceso,
  type AccionCuenta,
  type Ficha,
  type Institucion,
  type Perfil,
  type Relacion,
  type Usuario,
} from "@/shared/api";
import { t } from "@/shared/i18n";
import { fechaHora } from "@/shared/lib/fechas";
import {
  Aviso,
  Boton,
  Campo,
  Chip,
  Dialogo,
  ListaDatos,
  Plegable,
  Selector,
  Tabla,
  Vacio,
  type Columna,
} from "@/shared/ui";
import { useAccionCuenta, useCambiarPerfil, useConceder, useQuitar } from "../api/consultas";
import {
  accionesDisponibles,
  empresasParaAgregar,
  esDefinitiva,
  faltaMotivo,
  pideMotivo,
  separarAccesos,
} from "../model/usuarios";
import { EstadoUsuario, RUTA_INVITAR, type PrellenadoInvitacion } from "./Lista";
import { ElegirPerfil, ElegirRelacion, SelectorPerfil } from "./Piezas";
import estilos from "./Usuarios.module.css";

const f = t.ficha;

export function DatosUsuario({ usuario }: { readonly usuario: Usuario }) {
  return (
    <ListaDatos
      datos={[
        [f.correo, usuario.correo],
        [f.estado, <EstadoUsuario key="e" estado={usuario.estado} />],
        [f.ultimoIngreso, usuario.ultimoIngreso ? fechaHora(usuario.ultimoIngreso) : t.general.nunca],
        [f.invitadoPor, usuario.invitadoPor],
        [f.invitadoEn, fechaHora(usuario.invitadoEn)],
      ]}
    />
  );
}

/** Botones de la cuenta según su estado; el resultado se anuncia en un aviso que no roba el foco. */
export function AccionesCuenta({ usuario }: { readonly usuario: Usuario }) {
  const accion = useAccionCuenta(usuario.usuarioId);
  const navegar = useNavigate();
  const [pendiente, setPendiente] = useState<AccionCuenta | null>(null);
  const [hecha, setHecha] = useState<AccionCuenta | null>(null);
  const [motivo, setMotivo] = useState("");
  const disponibles = accionesDisponibles(usuario.estado);
  const textos = pendiente ? f.accionesCuenta[pendiente] : null;

  const cerrar = () => {
    setPendiente(null);
    setMotivo("");
    accion.reset();
  };
  const confirmar = (e: FormEvent) => {
    e.preventDefault();
    if (!pendiente) return;
    const elegida = pendiente;
    accion.mutate(
      { accion: elegida, motivo: pideMotivo(elegida) && motivo.trim() ? motivo.trim() : null },
      {
        onSuccess: () => {
          cerrar();
          setHecha(elegida);
        },
      },
    );
  };

  return (
    <>
      <div aria-live="polite">{hecha && <Aviso tono="bien">{f.accionesCuenta[hecha].listo}</Aviso>}</div>
      {usuario.estado === "baja" && (
        <div className={estilos.pila}>
          <p className={estilos.suave}>{f.bajaTexto}</p>
          <div>
            <Boton
              onClick={() => {
                const prellenado: PrellenadoInvitacion = { correo: usuario.correo, nombre: usuario.nombre };
                void navegar(RUTA_INVITAR, { state: prellenado });
              }}
            >
              {f.invitarDeNuevo}
            </Boton>
          </div>
        </div>
      )}
      {disponibles.length > 0 && (
        <div className={estilos.botones}>
          {disponibles.map((a) => (
            <Boton
              key={a}
              className={esDefinitiva(a) ? estilos.peligro : undefined}
              onClick={() => {
                setHecha(null);
                setPendiente(a);
              }}
            >
              {f.accionesCuenta[a].boton}
            </Boton>
          ))}
        </div>
      )}
      <Dialogo
        abierto={pendiente !== null}
        titulo={textos?.titulo ?? ""}
        descripcion={textos?.texto(usuario.correo)}
        alCerrar={cerrar}
      >
        <form className={estilos.formulario} onSubmit={confirmar}>
          {pendiente && pideMotivo(pendiente) && (
            <Campo
              rotulo={f.motivoAccion}
              value={motivo}
              maxLength={500}
              onChange={(e) => {
                setMotivo(e.target.value);
              }}
            />
          )}
          {accion.isError && (
            <Aviso tono="grave" rol="alert">
              {mensajeDeAccion(accion.error)}
            </Aviso>
          )}
          <div className={estilos.pie}>
            <Boton onClick={cerrar}>{t.general.cancelar}</Boton>
            <Boton
              type="submit"
              variante="principal"
              className={pendiente && esDefinitiva(pendiente) ? estilos.botonPeligro : undefined}
              disabled={accion.isPending}
            >
              {accion.isPending ? t.general.guardando : textos?.confirmar}
            </Boton>
          </div>
        </form>
      </Dialogo>
    </>
  );
}

/** Empresas que puede ver, con su perfil, relación y motivo; agregar y quitar. */
export function AccesosUsuario({
  ficha,
  habilitadas,
  nombres,
}: {
  readonly ficha: Ficha;
  readonly habilitadas: readonly Institucion[];
  readonly nombres: ReadonlyMap<string, string>;
}) {
  const { usuarioId, estado } = ficha.usuario;
  const cambiar = useCambiarPerfil(usuarioId);
  const quitar = useQuitar(usuarioId);
  const [aQuitar, setAQuitar] = useState<Acceso | null>(null);
  const { vigentes, quitados } = separarAccesos(ficha.accesos);
  const dadoDeBaja = estado === "baja";
  const nombre = (codigo: string) => nombres.get(codigo) ?? codigo;

  const columnas: Columna<Acceso>[] = [
    {
      clave: "empresa",
      titulo: f.columnaEmpresa,
      orden: (a) => nombre(a.institucion),
      celda: (a) => (
        <div className={estilos.persona}>
          <b>{nombre(a.institucion)}</b>
          <span className={estilos.correo}>{a.institucion}</span>
        </div>
      ),
    },
    {
      clave: "perfil",
      titulo: f.columnaPerfil,
      rotuloMovil: f.columnaPerfil,
      orden: (a) => t.perfiles[a.perfil],
      celda: (a) =>
        dadoDeBaja || a.estado === "baja" ? (
          t.perfiles[a.perfil]
        ) : (
          <SelectorPerfil
            rotulo={f.cambiarPerfil(nombre(a.institucion))}
            valor={a.perfil}
            deshabilitado={cambiar.isPending}
            alCambiar={(perfil) => {
              cambiar.mutate({ institucion: a.institucion, perfil });
            }}
          />
        ),
    },
    {
      clave: "relacion",
      titulo: f.columnaRelacion,
      rotuloMovil: f.columnaRelacion,
      orden: (a) => t.relaciones[a.relacion],
      celda: (a) => (
        <div className={estilos.persona}>
          <span>{t.relaciones[a.relacion]}</span>
          {a.motivo && <span className={estilos.correo}>{a.motivo}</span>}
        </div>
      ),
    },
    {
      clave: "concedido",
      titulo: f.columnaConcedido,
      rotuloMovil: f.columnaConcedido,
      orden: (a) => a.concedidoEn,
      celda: (a) => (
        <span className={estilos.suave}>
          {fechaHora(a.concedidoEn)} · {a.concedidoPor}
        </span>
      ),
    },
    {
      clave: "estado",
      titulo: f.columnaEstado,
      rotuloMovil: f.columnaEstado,
      celda: (a) => <Chip tono={a.estado === "activo" ? "bien" : "neutro"}>{t.estados[a.estado]}</Chip>,
      orden: (a) => t.estados[a.estado],
    },
  ];
  const conAcciones: Columna<Acceso>[] = dadoDeBaja
    ? columnas
    : [
        ...columnas,
        {
          clave: "acciones",
          titulo: f.acciones,
          rotuloMovil: f.acciones,
          celda: (a) => (
            <Boton
              chico
              variante="enlace"
              onClick={() => {
                quitar.reset();
                setAQuitar(a);
              }}
            >
              {f.quitar}
            </Boton>
          ),
        },
      ];

  return (
    <div className={estilos.pila}>
      {cambiar.isError && (
        <Aviso tono="grave" rol="alert">
          {mensajeDeAccion(cambiar.error)}
        </Aviso>
      )}
      {vigentes.length === 0 ? (
        <Vacio titulo={f.sinAccesosTitulo}>{f.sinAccesosTexto}</Vacio>
      ) : (
        <Tabla columnas={conAcciones} filas={vigentes} clave={(a) => a.institucion} textosOrden={t.tabla} />
      )}
      {quitados.length > 0 && (
        <Plegable resumen={f.quitadas(quitados.length)}>
          <Tabla columnas={columnas} filas={quitados} clave={(a) => `${a.institucion}-baja`} textosOrden={t.tabla} />
        </Plegable>
      )}
      {!dadoDeBaja && (
        <AgregarEmpresa usuarioId={usuarioId} disponibles={empresasParaAgregar(habilitadas, ficha.accesos)} />
      )}
      <Dialogo
        abierto={aQuitar !== null}
        titulo={aQuitar ? f.quitarTitulo(nombre(aQuitar.institucion)) : ""}
        descripcion={f.quitarTexto}
        alCerrar={() => {
          setAQuitar(null);
        }}
      >
        {quitar.isError && (
          <Aviso tono="grave" rol="alert">
            {mensajeDeAccion(quitar.error)}
          </Aviso>
        )}
        <div className={estilos.pie}>
          <Boton
            onClick={() => {
              setAQuitar(null);
            }}
          >
            {t.general.cancelar}
          </Boton>
          <Boton
            variante="principal"
            disabled={quitar.isPending}
            onClick={() => {
              if (aQuitar)
                quitar.mutate(aQuitar.institucion, {
                  onSuccess: () => {
                    setAQuitar(null);
                  },
                });
            }}
          >
            {quitar.isPending ? t.general.guardando : f.confirmarQuitar}
          </Boton>
        </div>
      </Dialogo>
    </div>
  );
}

function AgregarEmpresa({
  usuarioId,
  disponibles,
}: {
  readonly usuarioId: string;
  readonly disponibles: readonly Institucion[];
}) {
  const conceder = useConceder(usuarioId);
  const [abierto, setAbierto] = useState(false);
  const [institucion, setInstitucion] = useState("");
  const [perfil, setPerfil] = useState<Perfil>("operaciones");
  const [relacion, setRelacion] = useState<Relacion>("propia");
  const [motivo, setMotivo] = useState("");
  const [revisado, setRevisado] = useState(false);

  if (disponibles.length === 0) return <p className={estilos.suave}>{f.todasConcedidas}</p>;

  const elegida = institucion || disponibles[0]?.institucion || "";
  const sinMotivo = faltaMotivo(relacion, motivo);
  const cerrar = () => {
    setAbierto(false);
    setInstitucion("");
    setPerfil("operaciones");
    setRelacion("propia");
    setMotivo("");
    setRevisado(false);
    conceder.reset();
  };
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    setRevisado(true);
    if (sinMotivo) return;
    conceder.mutate({ institucion: elegida, perfil, relacion, motivo: motivo.trim() || null }, { onSuccess: cerrar });
  };

  return (
    <div>
      <Boton
        onClick={() => {
          setAbierto(true);
        }}
      >
        {f.agregar}
      </Boton>
      <Dialogo abierto={abierto} titulo={f.agregarTitulo} alCerrar={cerrar}>
        <form className={estilos.formulario} onSubmit={enviar} noValidate>
          <Selector
            rotulo={f.empresa}
            value={elegida}
            opciones={disponibles.map((x) => ({ valor: x.institucion, texto: `${x.nombre} (${x.institucion})` }))}
            onChange={(e) => {
              setInstitucion(e.target.value);
            }}
          />
          <ElegirRelacion valor={relacion} alCambiar={setRelacion} />
          <Campo
            rotulo={f.motivo}
            ayuda={f.motivoAyuda}
            value={motivo}
            maxLength={500}
            error={revisado && sinMotivo ? f.motivoFalta : null}
            onChange={(e) => {
              setMotivo(e.target.value);
            }}
          />
          <ElegirPerfil leyenda={f.columnaPerfil} valor={perfil} alCambiar={setPerfil} />
          {conceder.isError && (
            <Aviso tono="grave" rol="alert">
              {mensajeDeAccion(conceder.error)}
            </Aviso>
          )}
          <div className={estilos.pie}>
            <Boton onClick={cerrar}>{t.general.cancelar}</Boton>
            <Boton type="submit" variante="principal" disabled={conceder.isPending}>
              {conceder.isPending ? t.general.guardando : f.conceder}
            </Boton>
          </div>
        </form>
      </Dialogo>
    </div>
  );
}
